import { getCLS, getFID, getLCP, getFCP, getTTFB, Metric } from 'web-vitals';
import { API_BASE_URL } from '@config/api';

/**
 * Colectează Core Web Vitals și le trimite către backend
 */
export function initWebVitals() {
  // Largest Contentful Paint (LCP)
  getLCP((metric: Metric) => {
    sendWebVital({
      name: 'LCP',
      value: metric.value,
      rating: getRating(metric.name, metric.value),
      delta: metric.delta,
      id: metric.id,
      url: window.location.href
    });
  });

  // First Input Delay (FID)
  getFID((metric: Metric) => {
    sendWebVital({
      name: 'FID',
      value: metric.value,
      rating: getRating(metric.name, metric.value),
      delta: metric.delta,
      id: metric.id,
      url: window.location.href
    });
  });

  // Cumulative Layout Shift (CLS)
  getCLS((metric: Metric) => {
    sendWebVital({
      name: 'CLS',
      value: metric.value,
      rating: getRating(metric.name, metric.value),
      delta: metric.delta,
      id: metric.id,
      url: window.location.href
    });
  });

  // First Contentful Paint (FCP)
  getFCP((metric: Metric) => {
    sendWebVital({
      name: 'FCP',
      value: metric.value,
      rating: getRating(metric.name, metric.value),
      delta: metric.delta,
      id: metric.id,
      url: window.location.href
    });
  });

  // Time to First Byte (TTFB)
  getTTFB((metric: Metric) => {
    sendWebVital({
      name: 'TTFB',
      value: metric.value,
      rating: getRating(metric.name, metric.value),
      delta: metric.delta,
      id: metric.id,
      url: window.location.href
    });
  });
}

/**
 * Calculează rating-ul pentru o metrică Web Vital
 */
function getRating(name: string, value: number): string {
  // Threshold-uri bazate pe Web Vitals guidelines
  switch (name) {
    case 'LCP':
      return value <= 2500 ? 'good' : value <= 4000 ? 'needs-improvement' : 'poor';
    case 'FID':
      return value <= 100 ? 'good' : value <= 300 ? 'needs-improvement' : 'poor';
    case 'CLS':
      return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'FCP':
      return value <= 1800 ? 'good' : value <= 3000 ? 'needs-improvement' : 'poor';
    case 'TTFB':
      return value <= 800 ? 'good' : value <= 1800 ? 'needs-improvement' : 'poor';
    default:
      return 'unknown';
  }
}

/**
 * Trimite Web Vital către backend
 */
async function sendWebVital(vital: {
  name: string;
  value: number;
  rating: string;
  delta?: number;
  id?: string;
  url?: string;
}): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/errors/client/web-vitals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...vital,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok && response.status !== 404) {
      console.warn('Failed to send web vital:', vital.name);
    }
  } catch (error: any) {
    // Nu logăm eroarea dacă backend-ul nu rulează (ERR_CONNECTION_REFUSED)
    if (error?.message?.includes('Failed to fetch') || error?.name === 'TypeError') {
      // Backend-ul nu este disponibil - nu logăm pentru a evita spam în consolă
      return;
    }
    // Nu logăm eroarea pentru a evita loop infinit
    console.warn('Error sending web vital:', error);
  }
}

/**
 * Track performance pentru operațiuni custom
 */
export function trackPerformance(metricName: string, startTime: number): void {
  const duration = performance.now() - startTime;
  
  sendPerformanceMetric({
    metricType: 'operation',
    metricName,
    value: duration,
    url: window.location.href
  });
}

/**
 * Track API calls
 */
export function trackAPICall(endpoint: string, method: string, duration: number, success: boolean): void {
  sendPerformanceMetric({
    metricType: 'api_call',
    metricName: `${method} ${endpoint}`,
    value: duration,
    endpoint,
    method,
    success,
    url: window.location.href
  });
}

/**
 * Trimite metrică de performanță către backend
 */
async function sendPerformanceMetric(metric: {
  metricType: string;
  metricName: string;
  value: number;
  endpoint?: string;
  method?: string;
  success?: boolean;
  url?: string;
  metadata?: any;
}): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/errors/client/performance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...metric,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok && response.status !== 404) {
      console.warn('Failed to send performance metric:', metric.metricName);
    }
  } catch (error: any) {
    // Nu logăm eroarea dacă backend-ul nu rulează (ERR_CONNECTION_REFUSED)
    if (error?.message?.includes('Failed to fetch') || error?.name === 'TypeError') {
      // Backend-ul nu este disponibil - nu logăm pentru a evita spam în consolă
      return;
    }
    // Nu logăm eroarea pentru a evita loop infinit
    console.warn('Error sending performance metric:', error);
  }
}

/**
 * Hook pentru tracking API calls automat
 */
let isAPITrackingInitialized = false;

export function initAPITracking() {
  // Previne inițializarea multiplă
  if (isAPITrackingInitialized) {
    console.warn('initAPITracking() a fost deja inițializat. Sări peste re-inițializare.');
    return;
  }
  
  // Interceptează fetch calls
  const originalFetch = window.fetch;
  
  window.fetch = async function(...args: Parameters<typeof fetch>) {
    const startTime = performance.now();
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
    const method = typeof args[1] === 'object' && args[1]?.method ? args[1].method : 'GET';
    
    // Exclude tracking pentru endpoint-urile de metrici pentru a evita loop infinit
    const isPerformanceEndpoint = url.includes('/errors/client/performance') || 
                                   url.includes('/errors/client/web-vitals');
    
    try {
      const response = await originalFetch(...args);
      const duration = performance.now() - startTime;
      
      // Track doar API calls către backend-ul nostru, dar exclude endpoint-urile de metrici
      if (url.startsWith('/api/') && !isPerformanceEndpoint) {
        trackAPICall(url, method, duration, response.ok);
      }
      
      return response;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      if (url.startsWith('/api/') && !isPerformanceEndpoint) {
        trackAPICall(url, method, duration, false);
      }
      
      throw error;
    }
  };
  
  isAPITrackingInitialized = true;
}

/** Max ms for a valid page load duration (decimal(10,2) safe and realistic). */
const MAX_PAGE_LOAD_MS = 300000; // 5 min

/**
 * Track page load performance.
 * Folosește Navigation Timing v2 dacă e disponibil; altfel timing-ul deprecated, cu validare.
 */
export function trackPageLoad() {
  if (!window.performance) return;

  let pageLoadTime: number;
  let domContentLoaded = 0;
  let firstPaint = 0;

  const navEntries = performance.getEntriesByType?.('navigation');
  const nav = navEntries?.[0] as PerformanceNavigationTiming | undefined;
  if (nav && typeof nav.loadEventEnd === 'number' && nav.loadEventEnd > 0) {
    pageLoadTime = nav.loadEventEnd - nav.startTime;
    domContentLoaded = (nav.domContentLoadedEventEnd || 0) - nav.startTime;
  } else {
    const timing = (performance as Performance & { timing?: PerformanceTiming }).timing;
    if (!timing) return;
    pageLoadTime = timing.loadEventEnd - timing.navigationStart;
    domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
  }

  const paintEntries = performance.getEntriesByType?.('paint');
  if (paintEntries?.length) {
    const fp = paintEntries.find((e: PerformanceEntry & { name?: string }) => e.name === 'first-paint');
    firstPaint = fp ? (fp as PerformanceEntry & { startTime?: number }).startTime ?? 0 : 0;
  }

  // Evită valori invalide (deprecated timing poate da 0 sau negative)
  if (typeof pageLoadTime !== 'number' || pageLoadTime <= 0 || pageLoadTime > MAX_PAGE_LOAD_MS) {
    return;
  }

  sendPerformanceMetric({
    metricType: 'page_load',
    metricName: 'Page Load',
    value: pageLoadTime,
    url: window.location.href,
    metadata: {
      domContentLoaded,
      firstPaint
    }
  });
}

